import { useEffect, useMemo, useState } from 'react';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faPenToSquare, faFileArrowUp, faTrashCan, faPaperPlane, faUser } from '@fortawesome/free-solid-svg-icons';
import { api, ApiError } from '../../lib/api/client';
import { listDrafts, deleteDraft } from '../capture/drafts';
import { fmtDate, fmtDateTime, stateTone, typeLabel, canDelete, deleteBlockedReason } from '../../lib/transactions/display';
import { timeRemaining } from '../../lib/transactions/retention';

interface Txn {
  tcn: string;
  captureType: string;
  state: string;
  createdOn?: string;
  firstName?: string;
  lastName?: string;
  responseUri?: string;
  storageKey?: string;
  /** When an unstored card conversion will be deleted (ISO). Absent once it is in Secure Storage. */
  conversionExpiresOn?: string;
  /** Set when a photograph of the subject was stored with the transaction. */
  photoKey?: string;
  /** FBI-channel acknowledgment detail (DOCE). `submissionError` is the router refusing to send it. */
  ack?: {
    submissionError?: { reason?: string; detail?: string; submitterId?: string };
    response?: { error?: string; error_desc?: string };
  };
  ebtsKey?: string;
  resubmits?: { attempt: number; at: string }[];
}

/** Why a DOCE submission failed, for the operator to read before resubmitting. */
function failureReason(t: Txn): string | null {
  const se = t.ack?.submissionError;
  if (se?.reason) return [se.reason, se.detail].filter(Boolean).join(' — ');
  const r = t.ack?.response;
  if (r?.error) return [r.error, r.error_desc].filter(Boolean).join(' — ');
  return null;
}

/**
 * A rejected DOCE submission can be sent back to the FBI channel. It is never re-metered: the customer
 * paid when it was first submitted, so correcting a rejection is free.
 */
const canResubmit = (t: Txn) => t.captureType === 'doce' && t.state === 'ERROR';
interface TxnPage { items: Txn[]; cursor: string | null }
interface StorageStatus { active: boolean; tier: string | null; limit: number | null; used: number; migratable?: number }


/**
 * Saved-but-unfinished captures. These aren't transactions yet — no TCN, nothing submitted — so they
 * sit above the table rather than in it, and they disappear on submission or after 30 days.
 */
function DraftsPanel() {
  const qc = useQueryClient();
  const drafts = useQuery({ queryKey: ['drafts'], queryFn: listDrafts });
  const [removing, setRemoving] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function discard(id: string) {
    setRemoving(id); setErr(null);
    try {
      await deleteDraft(id);
      setConfirming(null);
      await qc.invalidateQueries({ queryKey: ['drafts'] });
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not delete that draft');
    } finally {
      setRemoving(null);
    }
  }

  const rows = drafts.data ?? [];
  if (rows.length === 0) return null;

  return (
    <div className="panel" style={{ marginBottom: 18 }}>
      <div className="panel-head">
        <h3 style={{ margin: 0 }}>Drafts</h3>
        <span className="usage-count">Unfinished captures · removed automatically after 30 days</span>
      </div>
      {err && <p className="cart-error">{err}</p>}
      <table className="tx-table">
        <thead><tr><th>Name</th><th>Workflow</th><th>Saved</th><th>Expires</th><th></th></tr></thead>
        <tbody>
          {rows.map((d) => {
            const name = [d.firstName, d.lastName].filter(Boolean).join(' ') || '(no name yet)';
            return (
              <tr key={d.draftId}>
                <td>{name}</td>
                <td>{typeLabel(d.captureType)}</td>
                <td>{fmtDate(d.createdOn)}</td>
                <td>{fmtDate(d.expiresOn)}</td>
                <td className="tx-actions">
                  {confirming === d.draftId ? (
                    <>
                      <span className="flow-hint">Delete this draft?</span>
                      <button className="btn btn-ghost btn-sm" disabled={removing === d.draftId}
                        onClick={() => void discard(d.draftId)}>
                        {removing === d.draftId ? 'Deleting…' : 'Delete'}
                      </button>
                      <button className="btn btn-ghost btn-sm" onClick={() => setConfirming(null)}>Cancel</button>
                    </>
                  ) : (
                    <>
                      <Link className="btn btn-primary btn-sm" to={`/app/capture/${d.captureType}?draft=${encodeURIComponent(d.draftId)}`}>
                        <FontAwesomeIcon icon={faPenToSquare} /> Edit
                      </Link>
                      <button className="btn btn-ghost btn-sm btn-icon" title="Delete draft"
                        onClick={() => setConfirming(d.draftId)}>
                        <FontAwesomeIcon icon={faTrashCan} />
                      </button>
                    </>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const PAGE_SIZE = 50;

export function Transactions() {
  const [term, setTerm] = useState('');
  // Debounced so each keystroke doesn't start a fresh sweep of the account's history.
  const [query, setQuery] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setQuery(term.trim()), 300);
    return () => clearTimeout(t);
  }, [term]);

  /*
   * Paged, and searched SERVER-side: the term goes to the API so a search covers the whole history.
   * Filtering in the browser only ever saw the rows already fetched, so anything past the last
   * "Load more" was invisible to search.
   */
  const txns = useInfiniteQuery({
    queryKey: ['transactions', query],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => {
      const qs = new URLSearchParams({ limit: String(PAGE_SIZE) });
      if (pageParam) qs.set('cursor', pageParam);
      if (query) qs.set('q', query);
      return api.get<TxnPage>(`/transactions?${qs.toString()}`);
    },
    getNextPageParam: (last) => last.cursor ?? undefined,
  });
  const status = useQuery({ queryKey: ['storage-status'], queryFn: () => api.get<StorageStatus>('/storage/status') });
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [migrating, setMigrating] = useState(false);

  async function migrateNow() {
    setMigrating(true); setErr(null);
    try {
      await api.post('/storage/migrate', {});
      await Promise.all([txns.refetch(), status.refetch()]);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Migration failed');
    } finally {
      setMigrating(false);
    }
  }

  /*
   * The photograph is a separate artifact, so it has its own action. Always a presigned URL — unlike an
   * ephemeral conversion, a stored photograph is not consumed by being fetched, so it can be downloaded
   * as many times as the customer likes.
   */
  async function downloadPhoto(t: Txn) {
    setBusy(`${t.tcn}-photo`);
    setErr(null);
    try {
      const res = await api.get<{ url: string; filename: string }>(
        `/transactions/${encodeURIComponent(t.tcn)}/photo`);
      const a = document.createElement('a');
      a.href = res.url;
      a.download = res.filename ?? 'photo.png';
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      setTimeout(() => a.remove(), 1500);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not download the photograph');
    } finally {
      setBusy(null);
    }
  }

  async function download(t: Txn) {
    setBusy(t.tcn);
    setErr(null);
    try {
      const res = await api.get<{ url?: string; data?: string; filename: string; contentType?: string }>(
        `/transactions/${encodeURIComponent(t.tcn)}/download`);
      let href = res.url;
      let objectUrl: string | null = null;
      if (!href && res.data) {
        // Ephemeral conversion returned inline (and deleted server-side) — build a blob to download.
        const bin = atob(res.data);
        const bytes = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
        objectUrl = URL.createObjectURL(new Blob([bytes], { type: res.contentType ?? 'application/octet-stream' }));
        href = objectUrl;
      }
      if (!href) throw new Error('No download available');
      const a = document.createElement('a');
      a.href = href;
      a.download = res.filename ?? 'eft.eft';
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { a.remove(); if (objectUrl) URL.revokeObjectURL(objectUrl); }, 1500);
      // If it was a one-time (ephemeral) download, refresh so the row reflects it's now gone.
      if (res.data) await Promise.all([txns.refetch(), status.refetch()]);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Download failed');
    } finally {
      setBusy(null);
    }
  }

  async function remove(t: Txn) {
    if (!window.confirm(
      `Delete transaction ${t.tcn}?\n\n` +
      `It will be removed from your list${t.storageKey ? ' and its stored EFT deleted' : ''}. ` +
      'A record is kept for 90 days so it can be matched against your billing, then erased.',
    )) return;
    setBusy(`del-${t.tcn}`);
    setErr(null);
    try {
      await api.del(`/transactions/${encodeURIComponent(t.tcn)}`);
      await Promise.all([txns.refetch(), status.refetch()]);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Delete failed');
    } finally {
      setBusy(null);
    }
  }

  /** Re-queue a rejected DOCE submission as-is. Corrections go through Edit first, which rewrites the EBTS. */
  async function resubmitToFbi(t: Txn) {
    const reason = failureReason(t);
    if (!window.confirm(
      `Resubmit ${t.tcn} to the FBI channel?\n\n${reason ? `Previous failure: ${reason}\n\n` : ''}` +
      'This sends the current file again and is not charged. If the data itself needs correcting, use Edit first.',
    )) return;
    setBusy(`re-${t.tcn}`);
    setErr(null);
    try {
      await api.post('/doce/resubmit', { tcn: t.tcn });
      await txns.refetch();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Resubmit failed');
    } finally {
      setBusy(null);
    }
  }

  const canDownload = (t: Txn) => t.state === 'COMPLETE' && (Boolean(t.responseUri) || Boolean(t.storageKey));

  // Every loaded page, already matched server-side — no client-side filtering left to do.
  const filtered = useMemo(() => txns.data?.pages.flatMap((p) => p.items) ?? [], [txns.data]);
  const loaded = filtered.length;
  /**
   * The server stopped early at its scan bound; more may lie further back.
   *
   * Not only for searches. An Operator's list is narrowed to their own captures by the same bounded
   * sweep, so their first page can come back empty in a busy office while their transactions sit further
   * down the history — "No transactions yet" would be a lie with a Load more button under it.
   */
  const partial = Boolean(txns.hasNextPage);

  /**
   * Conversions on the clock, summarised for the storage prompt.
   *
   * Counted from the page already loaded rather than a new endpoint: the prompt is a nudge, and one that
   * says "at least these" is honest and costs nothing. `soonest` drives the deadline in the copy.
   */
  const expiring = useMemo(() => {
    const live = filtered
      .filter((t) => !t.storageKey)
      .map((t) => timeRemaining(t.conversionExpiresOn))
      .filter((r): r is NonNullable<typeof r> => Boolean(r) && !r!.expired);
    const soonest = live.reduce<null | (typeof live)[number]>(
      (best, r) => (!best || r.daysRemaining < best.daysRemaining ? r : best),
      null,
    );
    return { count: live.length, soonest, soonestLabel: soonest ? soonest.label.toLowerCase() : '' };
  }, [filtered]);

  const s = status.data;
  const pct = s?.limit ? Math.min(100, Math.round((s.used / s.limit) * 100)) : 0;
  const full = s?.active && s.limit != null && s.used >= s.limit;
  const near = s?.active && s.limit != null && !full && s.used / s.limit >= 0.8;

  return (
    <div>
      <div className="console-head">
        <div>
          <h1>Transactions</h1>
          <div className="sub">Your EFT transactions and card conversions — search and download completed files.</div>
        </div>
        <Link to="/app/tools/correct-eft" className="btn btn-ghost">Correct an EFT file</Link>
      </div>

      <DraftsPanel />

      {/* Secure storage usage / prompt */}
      {s?.active ? (
        <div className="panel" style={{ marginBottom: 18 }}>
          <div className="panel-head">
            <h3 style={{ margin: 0 }}>Secure storage{s.tier ? ` · ${s.tier}` : ''}</h3>
            <span className="usage-count">
              {s.used.toLocaleString()}{s.limit != null ? ` / ${s.limit.toLocaleString()}` : ''} EFTs ·{' '}
              <Link to="/app/subscriptions" className="track-link">Manage plan</Link>
            </span>
          </div>
          {s.limit != null && (
            <div className="usage-bar" style={{ marginTop: 14 }}>
              <div className={`usage-fill${full ? ' full' : near ? ' near' : ''}`} style={{ width: `${pct}%` }} />
            </div>
          )}
          {full && (
            <div className="banner-warn" style={{ marginTop: 14 }}>
              Your {s.tier ?? 'storage'} plan is full — new EFTs won't be saved until you{' '}
              <Link to="/app/subscriptions" className="track-link">upgrade</Link>.
            </div>
          )}
        </div>
      ) : (
        /*
         * One consolidated call to action. This used to be two panels stacked on top of each other, each
         * ending in its own "Enable storage" button pointing at the same page — the un-migrated-EFT
         * warning is a reason to subscribe, not a separate decision, so it belongs inside this panel.
         */
        <div className="panel storage-cta-stack" style={{ marginBottom: 18 }}>
          <div>
            <strong>Secure storage is off.</strong>{' '}
            <span style={{ color: 'var(--text-dim)' }}>Subscribe to automatically save every EFT and search your archive by name or TCN.</span>
          </div>
          {/*
            * The concrete stake, above the general pitch. "Subscribe to save every EFT" is abstract;
            * "3 conversions will be deleted, the soonest in 9 days" is a decision with a deadline, and a
            * deadline is what customers had no way to see before.
            */}
          {expiring.count > 0 && (
            <div className="banner-warn">
              <strong>
                {expiring.count} card conversion{expiring.count === 1 ? '' : 's'} will be deleted
                {expiring.soonest ? ` — the soonest ${expiring.soonestLabel}` : ''}.
              </strong>{' '}
              Conversions are kept for 30 days unless you have Secure Storage. Subscribe now and
              {expiring.count === 1 ? ' it stays' : ' they stay'} available.
            </div>
          )}
          {(s?.migratable ?? 0) > 0 && (
            <div className="banner-warn">
              <strong>{s!.migratable!.toLocaleString()} EFT{s!.migratable === 1 ? '' : 's'} not yet in Secure Storage.</strong>{' '}
              This includes data imported from your previous account. Subscribe to Secure Storage to keep{' '}
              {s!.migratable === 1 ? 'it' : 'them'} available in EFTCreator.
            </div>
          )}
          <Link to="/app/subscriptions" className="btn btn-primary btn-sm storage-cta-action">Enable storage →</Link>
        </div>
      )}

      {/* Migrate un-stored / imported v1 EFTs into secure storage. Only with storage ON — this is the
          migrate action, distinct from subscribing; when storage is off it is folded into the CTA above. */}
      {s?.active && (s.migratable ?? 0) > 0 && (
        <div className="panel" style={{ marginBottom: 18, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <strong>{s.migratable!.toLocaleString()} EFT{s.migratable === 1 ? '' : 's'} not yet in Secure Storage.</strong>{' '}
            <span style={{ color: 'var(--text-dim)' }}>
              This includes data imported from your previous account. Move it into Secure Storage to keep it searchable and durable here.
            </span>
          </div>
          <button className="btn btn-primary btn-sm" onClick={migrateNow} disabled={migrating || full}>
            {migrating ? 'Migrating…' : full ? 'Storage full' : 'Migrate to storage'}
          </button>
        </div>
      )}

      {err && <p className="cart-error">{err}</p>}

      {/* Search, table and paging share one panel — the search belongs to the list it filters. */}
      <div className="panel" style={{ padding: 0, overflow: 'hidden' }}>
        <div className="tx-panel-head">
          <form className="storage-search" onSubmit={(e) => e.preventDefault()}>
            <label className="field"><span className="field-label">Search name or TCN</span>
              <input className="input" value={term} onChange={(e) => setTerm(e.target.value)} placeholder="e.g. Smith" /></label>
            {term && (
              <button type="button" className="btn btn-ghost" onClick={() => setTerm('')}>Clear</button>
            )}
          </form>
        </div>

        {txns.isLoading ? (
          <p className="loading-screen">// loading transactions…</p>
        ) : txns.isError ? (
          /*
           * An error must not render as "No transactions yet".
           *
           * That is exactly how this failed: the list demanded a capability Operators do not hold, every
           * request 403'd, and the page fell through to the empty state — so a permission fault was
           * reported as "I have no transactions", which is a very different bug to go looking for.
           */
          <div className="panel empty" style={{ boxShadow: 'none' }}>
            <h3>We couldn't load your transactions</h3>
            <p>{txns.error instanceof ApiError ? txns.error.message : 'Something went wrong.'}</p>
            <button className="btn btn-ghost btn-sm" onClick={() => void txns.refetch()}>Try again</button>
          </div>
        ) : loaded === 0 && query ? (
          <div className="panel empty" style={{ boxShadow: 'none' }}>
            <h3>No matching transactions</h3>
            {/* A search can stop at the server's scan bound before reaching the end of the history, so
                distinguish "nothing so far" from "nothing at all". */}
            <p>{partial
              ? 'No matches yet — keep searching to look further back.'
              : 'Try different search terms.'}</p>
          </div>
        ) : loaded === 0 ? (
          <div className="panel empty" style={{ boxShadow: 'none' }}>
            <h3>{partial ? 'Nothing found yet' : 'No transactions yet'}</h3>
            <p>{partial
              ? 'Keep looking to search further back through the history.'
              : 'Captured EFTs and conversions will appear here.'}</p>
          </div>
        ) : (
          <table className="tx-table">
            <thead>
              {/* No date of birth here, by column or by filter: it is PII this screen doesn't need, and
                  the filter could never match anyway — DOCE submissions discard dob and v1-migrated
                  records never carried one. DOB is still readable on a single transaction's edit screen,
                  where it is the data being corrected. */}
              <tr><th>Created</th><th>Name</th><th>Type</th><th>TCN</th><th>Status</th><th></th></tr>
            </thead>
            <tbody>
              {filtered.map((t) => (
                <tr key={t.tcn}>
                  {/* Shared with the admin view via `display`, so a customer and the agent helping them
                      never read two different timestamps for the same transaction. */}
                  <td className="tx-created">{fmtDateTime(t.createdOn)}</td>
                  <td>
                    {[t.lastName, t.firstName].filter(Boolean).join(', ') || '—'}
                    {t.storageKey && <span className="pill pill-scan stored-pill">Stored</span>}
                    {/*
                      * An unstored conversion is on a clock. Shown next to the name rather than buried in
                      * a tooltip: the customer's decision — download it, or subscribe to keep it — has a
                      * deadline, and they cannot make it if they cannot see it.
                      */}
                    {(() => {
                      const left = t.storageKey ? null : timeRemaining(t.conversionExpiresOn);
                      if (!left) return null;
                      return (
                        <span className={`pill retention-pill${left.urgent ? ' is-urgent' : ''}`} title={`Deleted on ${fmtDateTime(t.conversionExpiresOn!)} unless saved to Secure Storage`}>
                          {left.label}
                        </span>
                      );
                    })()}
                  </td>
                  <td>{typeLabel(t.captureType)}</td>
                  <td className="mono tx-tcn">{t.tcn}</td>
                  <td>
                    <span
                      className={`status-badge tone-${stateTone(t.state)}`}
                      {...(failureReason(t) ? { title: failureReason(t) as string } : {})}
                    >
                      {t.state}
                    </span>
                    {/* The reason a submission was rejected is what decides whether the fix is Edit or
                        a plain Resubmit, so it belongs on the row rather than behind a tooltip alone. */}
                    {canResubmit(t) && failureReason(t) && (
                      <div className="tx-fail-reason">{failureReason(t)}</div>
                    )}
                    {Boolean(t.resubmits?.length) && (
                      <div className="tx-fail-reason">
                        Resubmitted {t.resubmits!.length}×
                      </div>
                    )}
                  </td>
                  <td className="tx-actions">
                    {canDownload(t) ? (
                      <button className="btn btn-primary btn-icon btn-sm" onClick={() => download(t)} disabled={busy === t.tcn}
                        title="Download EFT" aria-label="Download EFT">
                        {busy === t.tcn ? '…' : <FontAwesomeIcon icon={faDownload} />}
                      </button>
                    ) : t.state === 'SUBMITTED' && t.captureType.startsWith('conv') ? (
                      <span style={{ color: 'var(--text-faint)', fontSize: 13 }}>Processing…</span>
                    ) : t.state === 'IN_REVIEW' ? (
                      /* Says a person has it. "Processing…" for days is what this state exists to stop. */
                      <span style={{ color: 'var(--text-faint)', fontSize: 13 }} title="Our team is converting this card by hand — we'll email you when it's ready.">
                        With our team
                      </span>
                    ) : null}
                    {/* Shown only when a photograph was actually stored, so its absence is never ambiguous. */}
                    {t.photoKey && (
                      <button className="btn btn-ghost btn-icon btn-sm" onClick={() => downloadPhoto(t)}
                        disabled={busy === `${t.tcn}-photo`}
                        title="Download the subject's photograph" aria-label="Download photograph">
                        {busy === `${t.tcn}-photo` ? '…' : <FontAwesomeIcon icon={faUser} />}
                      </button>
                    )}
                    {canResubmit(t) && (
                      <>
                        {/* Edit rewrites the EBTS's Type-2 data server-side (prints untouched), then the
                            operator resubmits the corrected file. */}
                        <Link
                          className="btn btn-ghost btn-icon btn-sm"
                          to={`/app/transactions/${encodeURIComponent(t.tcn)}/edit`}
                          title="Correct the descriptive data before resubmitting"
                          aria-label="Correct descriptive data"
                        >
                          <FontAwesomeIcon icon={faPenToSquare} />
                        </Link>
                        <button
                          className="btn btn-primary btn-icon btn-sm"
                          onClick={() => resubmitToFbi(t)}
                          disabled={busy === `re-${t.tcn}`}
                          title="Resubmit to the FBI channel (not charged again)"
                          aria-label="Resubmit to the FBI channel"
                        >
                          {busy === `re-${t.tcn}` ? '…' : <FontAwesomeIcon icon={faPaperPlane} />}
                        </button>
                      </>
                    )}
                    {t.storageKey && t.state === 'COMPLETE' ? (
                      <Link
                        className="btn btn-ghost btn-icon btn-sm"
                        to={`/app/transactions/${encodeURIComponent(t.tcn)}/edit`}
                        title="Edit descriptive data (fingerprints unchanged)"
                        aria-label="Edit descriptive data"
                      >
                        <FontAwesomeIcon icon={faPenToSquare} />
                      </Link>
                    ) : canDownload(t) ? (
                      <Link
                        className="btn btn-ghost btn-icon btn-sm"
                        to="/app/tools/correct-eft"
                        title="Not in secure storage — download this EFT, then upload it here to correct its details"
                        aria-label="Correct an EFT file"
                      >
                        <FontAwesomeIcon icon={faFileArrowUp} />
                      </Link>
                    ) : null}
                    {/* Disabled rather than hidden while the work is in flight: the row still needs to
                        explain why it cannot be removed yet, and a button that vanishes reads as a bug. */}
                    <button
                      className="btn btn-ghost btn-icon btn-sm"
                      onClick={() => remove(t)}
                      disabled={busy === `del-${t.tcn}` || !canDelete(t.state)}
                      title={deleteBlockedReason(t.state) ?? 'Delete transaction'}
                      aria-label={deleteBlockedReason(t.state) ?? 'Delete transaction'}
                    >
                      {busy === `del-${t.tcn}` ? '…' : <FontAwesomeIcon icon={faTrashCan} />}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {txns.hasNextPage && (
          <div className="tx-panel-foot">
            <button className="btn btn-ghost btn-sm" onClick={() => void txns.fetchNextPage()}
              disabled={txns.isFetchingNextPage}>
              {txns.isFetchingNextPage
                ? (query ? 'Searching…' : 'Loading…')
                : (query ? 'Keep searching' : loaded === 0 ? 'Keep looking' : `Load ${PAGE_SIZE} more`)}
            </button>
            <span className="field-help">
              {query ? `${loaded} match${loaded === 1 ? '' : 'es'} so far` : `${loaded} loaded`}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
